if (!customElements.get('product-combinations')) {
  class ProductCombinations extends HTMLElement {
    constructor() {
      super();
      this.checkboxes = [];
      this.combinationsBody = null;
      this.combinationsFooter = null;
      this.submitControls = null;
      this.submitButton = null;
      this.mainPrice = 0;
      this.currency = '';
      this.exchangeRate = 1.95583;
      this.productItems = [];
      this.variantSelects = [];
    }  

    connectedCallback() {
      this.initializeElements();
      this.attachEventListeners();
      this.checkInitialAvailability();
      this.updateState();
    }

    initializeElements() {
      this.checkboxes = this.querySelectorAll('input[type="checkbox"]');      
      this.combinationsBody = this.querySelector('[data-combinations-body]');
      this.combinationsFooter = this.querySelector('[data-combinations-footer]');
      this.submitControls = document.querySelector('.product-form__controls-group--submit');
      this.submitButton = this.querySelector('[data-submit-button]');
      
      // Get main price, currency, and exchange rate
      this.mainPrice = parseInt(this.dataset.mainPrice) || 0;
      this.currency = this.dataset.currency || 'BGN';
      this.exchangeRate = parseFloat(this.dataset.exchangeRate) || 1.95583;
      
      // Get all product items and variant selects
      this.productItems = this.querySelectorAll('.product-combinations__item');
      this.variantSelects = this.querySelectorAll('.product-entry__select');
    }

    attachEventListeners() {
      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateState();
          this.updatePrices();
        });
      });
      
      // Add event listeners for variant select changes
      this.variantSelects.forEach(select => {
        select.addEventListener('change', () => {
          this.handleVariantChange(select);
        });
      });
      
      // Add event listener for submit button
      if (this.submitButton) {
        this.submitButton.addEventListener('click', (e) => {
          // Set flag to indicate combinations button was clicked
          if (window.productCombinationsState) {
            window.productCombinationsState.isCombinationsClicked = true;
          }

          e.preventDefault();
          this.handleSubmit();
        });
      }
    }

    updateState() {
      const hasCheckedBoxes = Array.from(this.checkboxes).some(checkbox => checkbox.checked);
      
      if (hasCheckedBoxes) {
        this.showCombinations();
      } else {
        this.hideCombinations();
      }
    }

    showCombinations() {
      if (this.combinationsBody) {
        this.combinationsBody.classList.add('show');
      }
      
      if (this.combinationsFooter) {
        this.combinationsFooter.classList.remove('hidden');
      }
    }

    hideCombinations() {
      if (this.combinationsBody) {
        this.combinationsBody.classList.remove('show');
      }
      
      if (this.combinationsFooter) {
        this.combinationsFooter.classList.add('hidden');
      }
    }

    handleVariantChange(select) {
      const productId = select.dataset.productId;
      const productItem = this.querySelector(`[data-product-id="${productId}"]`);
      
      if (!productItem) return;
      
      // Update availability for all selects in this product
      this.updateProductAvailability(productItem);
      
      // Get the selected variant
      const selectedVariant = this.getSelectedVariant(productItem, select);
      if (!selectedVariant) return;
      
      // Update the hidden variant ID input
      const variantIdInput = productItem.querySelector('[data-variant-id]');
      if (variantIdInput) {
        variantIdInput.value = selectedVariant.id;
      }
      
      // Update the price display
      this.updateProductPrice(productItem, selectedVariant);
      
      // Update total prices if this product is checked
      const checkbox = productItem.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        this.updatePrices();
      }
    }

    getSelectedVariant(productItem, changedSelect) {
      const productId = productItem.dataset.productId;
      const scriptTag = this.querySelector(`script[data-product-variants="${productId}"]`);
      
      if (!scriptTag) return null;
      
      try {
        const variants = JSON.parse(scriptTag.textContent);
        const selects = productItem.querySelectorAll('.product-entry__select');
        
        // Build the selected options object
        const selectedOptions = {};
        selects.forEach(select => {
          const optionIndex = parseInt(select.dataset.optionIndex);
          selectedOptions[optionIndex] = select.value;
        });
        
        // Find matching variant
        return variants.find(variant => {
          return variant.options.every((option, index) => {
            return option === selectedOptions[index];
          });
        });
      } catch (error) {
        console.error('Error parsing variant data:', error);
        return null;
      }
    }

    updateProductPrice(productItem, variant) {
      const priceElement = productItem.querySelector('.product-entry__price');
      if (!priceElement || !variant) return;
      
      // Update the data attribute
      priceElement.dataset.productPrice = variant.price;
      
      // Format and update the display price
      const formattedPrice = this.formatPrice(variant.price);
      priceElement.innerHTML = formattedPrice;
    }

    updatePrices() {
      const additionalPrice = this.calculateAdditionalPrice();
      const totalPrice = additionalPrice + this.mainPrice;
      
      // Update additional price display
      const additionalElement = this.querySelector('[data-combinations-additional]');
      if (additionalElement) {
        additionalElement.textContent = this.formatPrice(additionalPrice);
      }
      
      // Update total price displays
      const totalElements = this.querySelectorAll('[data-combinations-total]');
      totalElements.forEach(element => {
        element.textContent = this.formatPrice(totalPrice);
      });
    }

    calculateAdditionalPrice() {
      let totalAdditionalPrice = 0;
      
      this.checkboxes.forEach(checkbox => {
        if (checkbox.checked) {
          const productId = checkbox.value;
          const productItem = this.querySelector(`[data-product-id="${productId}"]`);
          
          if (productItem) {
            const priceElement = productItem.querySelector('.product-entry__price');
            if (priceElement) {
              const price = parseInt(priceElement.dataset.productPrice) || 0;
              totalAdditionalPrice += price;
            }
          }
        }
      });
      
      return totalAdditionalPrice;
    }

    formatPrice(priceInCents) {
      const bgnPrice = priceInCents / 100;
      const eurPrice = bgnPrice / this.exchangeRate;
      
      // Format EUR price with proper decimal handling (matching the Liquid logic)
      const eurPriceString = eurPrice.toFixed(5);
      let eurPriceDisplay;
      
      if (eurPriceString.includes('.')) {
        const priceParts = eurPriceString.split('.');
        const integerPart = priceParts[0];
        const decimalPart = priceParts[1];
        const truncatedDecimal = decimalPart.substring(0, 2);
        eurPriceDisplay = `${integerPart}.${truncatedDecimal}`;
      } else {
        eurPriceDisplay = eurPriceString;
      }
      
      return `${bgnPrice.toFixed(2)} лв. ( € ${eurPriceDisplay} )`;
    }

    checkInitialAvailability() {
      this.productItems.forEach(productItem => {
        this.updateProductAvailability(productItem);
      });
    }

    updateProductAvailability(productItem) {
      const productId = productItem.dataset.productId;
      const scriptTag = this.querySelector(`script[data-product-variants="${productId}"]`);
      
      if (!scriptTag) return;
      
      try {
        const variants = JSON.parse(scriptTag.textContent);
        const selects = productItem.querySelectorAll('.product-entry__select');
        
        // Get current selections
        const currentSelections = {};
        selects.forEach(select => {
          const optionIndex = parseInt(select.dataset.optionIndex);
          currentSelections[optionIndex] = select.value;
        });
        
        // Update each select's options availability
        selects.forEach(select => {
          this.updateSelectAvailability(select, variants, currentSelections);
        });
        
        // Check if current variant is available
        const currentVariant = this.getSelectedVariant(productItem, null);
        if (currentVariant && !this.isVariantAvailable(currentVariant)) {
          // If current variant is not available, try to find an available one
          this.selectAvailableVariant(productItem, variants);
        }
        
      } catch (error) {
        console.error('Error checking product availability:', error);
      }
    }

    updateSelectAvailability(select, variants, currentSelections) {
      const optionIndex = parseInt(select.dataset.optionIndex);
      const options = select.querySelectorAll('option');
      
      options.forEach(option => {
        // Create a test selection with this option
        const testSelections = { ...currentSelections };
        testSelections[optionIndex] = option.value;
        
        // Check if any variant exists with this combination
        const hasAvailableVariant = variants.some(variant => {
          const matchesSelection = variant.options.every((variantOption, index) => {
            return variantOption === testSelections[index];
          });
          return matchesSelection && this.isVariantAvailable(variant);
        });
        
        // Enable/disable the option
        option.disabled = !hasAvailableVariant;
        if (!hasAvailableVariant) {
          option.style.color = '#ccc';
        } else {
          option.style.color = '';
        }
      });
    }

    isVariantAvailable(variant) {
      // Check if variant is available (has inventory or allows backorders)
      return variant.available || variant.inventory_policy === 'continue';
    }

    selectAvailableVariant(productItem, variants) {
      const selects = productItem.querySelectorAll('.product-entry__select');
      
      // Find the first available variant
      const availableVariant = variants.find(variant => this.isVariantAvailable(variant));
      
      if (availableVariant) {
        // Update all selects to match the available variant
        selects.forEach(select => {
          const optionIndex = parseInt(select.dataset.optionIndex);
          const optionValue = availableVariant.options[optionIndex];
          
          if (optionValue) {
            select.value = optionValue;
          }
        });
        
        // Update the hidden variant ID input
        const variantIdInput = productItem.querySelector('[data-variant-id]');
        if (variantIdInput) {
          variantIdInput.value = availableVariant.id;
        }
        
        // Update the price display
        this.updateProductPrice(productItem, availableVariant);
      }
    }
    
    handleSubmit() {
      // Find the main product form submit button or form
      const submitButton = document.querySelector('.product-form__cart-submit');
      
      if (submitButton) {
        submitButton.click();
      }
    }
  }

  customElements.define('product-combinations', ProductCombinations);
}